import { useState } from "react";

import { Button, Spinner } from "@heroui/react";
import { Icon } from "@iconify/react";

import { OpenMode, OperationDrawer } from "@onesolution/components";
import {
	useClientInfos,
	useIsUserInRoles,
	useLanguage,
} from "@onesolution/hooks";

import type { OperationDetailsTabs } from "@onesolution/components/OperationDrawer/OperationDetails";
import { FEATURE_FLAGS, USER_ROLES } from "@onesolution/constants";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import OperationTable from "./OperationTable";

export default function OperationsPage() {
	const { t } = useLanguage();
	const clientInfos = useClientInfos();
	const canEdit = useIsUserInRoles([USER_ROLES.Admin, USER_ROLES.Member]);
	const search: { operation: string; tab: string } = useSearch({
		strict: false,
	});
	const [drawerState, setDrawerState] = useState({
		mode: search.operation ? OpenMode.details : OpenMode.close,
		defaultTab: (search.tab ? search.tab : "overview") as OperationDetailsTabs,
		selectedOpId: search.operation ? search.operation.toString() : "",
		onlyChat: false,
	});
	const queryClient = useQueryClient();
	const isFetching = useIsFetching({
		queryKey: ["infinite"],
	});
	return (
		<div className="size-full flex flex-col">
			<div className="flex justify-between w-full mb-6">
				<div className="flex flex-row gap-2 items-center justify-center">
					<h1 className="text-3xl font-bold">{t("name.operations")}</h1>

					<Button
						isIconOnly
						variant="light"
						size="sm"
						isLoading={Boolean(isFetching)}
						isDisabled={Boolean(isFetching)}
					>
						{isFetching ? (
							<Spinner size="sm" color="primary" />
						) : (
							<Icon
								icon="solar:refresh-line-duotone"
								className="size-5 text-gray-400"
								onClick={() =>
									queryClient.invalidateQueries({
										queryKey: ["operations"],
									})
								}
							/>
						)}
					</Button>
				</div>
				{canEdit &&
					!clientInfos?.disabledFeatures.includes(
						FEATURE_FLAGS.createOperation,
					) && (
						<Button
							variant="shadow"
							color="primary"
							startContent={
								<Icon icon="solar:add-circle-bold" className="size-5" />
							}
							onPress={() =>
								setDrawerState({ ...drawerState, mode: OpenMode.creation })
							}
						>
							{t("basic.create")} {t("name.operation")}
						</Button>
					)}
			</div>

			<OperationTable
				onCreateOperationClick={() =>
					setDrawerState({ ...drawerState, mode: OpenMode.creation })
				}
				onRowClick={(id, defaultTab, onlyChat) => {
					setDrawerState({
						mode: OpenMode.details,
						selectedOpId: id,
						defaultTab: defaultTab || "overview",
						onlyChat: onlyChat || false,
					});
				}}
			/>
			<OperationDrawer
				mode={drawerState.mode}
				selectedOpId={drawerState.selectedOpId}
				defaultOpenedTab={drawerState.defaultTab}
				onlyChat={drawerState.onlyChat}
				onEdit={() =>
					setDrawerState({
						...drawerState,
						mode: OpenMode.edition,
					})
				}
				onClose={() =>
					setDrawerState({
						...drawerState,
						mode: OpenMode.close,
						selectedOpId: "",
					})
				}
			/>
		</div>
	);
}
